/**
 * ExecutionRecipePanel
 *
 * Surfaces the execution recipe implied by an item's work_type (Canon Doc 21) —
 * approach kind, uncertainty multiplier, required process artifacts, and
 * governance policies — read directly from the same execution_recipe_templates
 * registry that Planning resolves server-side.
 *
 * This is informational: it exposes what the work type *means* for execution so
 * the expectation is visible where people work, rather than only inside Planning.
 * It does not track per-item artifact completion (there is no backing store for
 * that), so it states what the recipe requires without pretending to know what
 * has been produced.
 */

import { useEffect } from 'react';
import { Wrench, FlaskConical, AlertTriangle, CircleDashed, ShieldCheck } from 'lucide-react';
import { useExecutionRecipeStore } from '../../../stores/prioritization/executionRecipeStore';
import { useWorkTypeCatalog } from '../../../hooks/prioritization/useWorkTypeCatalog';
import { APPROACH_KIND_LABELS } from '../../../types/work-type-recipes';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useModuleFeatureAccessible } from '../../../lib/workspaceModulesService';
import { FEATURE_FLAGS } from '../../../lib/featureFlags';

export function ExecutionRecipePanel({ workType }: { workType: string | null | undefined }) {
  const accessible = useModuleFeatureAccessible('execution_recipe', FEATURE_FLAGS.EXECUTION_RECIPE);
  const workspaceId = getCurrentWorkspaceId();
  const { getLabel } = useWorkTypeCatalog(workspaceId);
  const { loading, loaded, fetchRecipes, getRecipeByWorkType } = useExecutionRecipeStore();

  useEffect(() => {
    if (!accessible) return;
    void fetchRecipes(workspaceId);
  }, [accessible, fetchRecipes, workspaceId]);

  if (!accessible) return null;

  const label = workType ? getLabel(workType) : null;

  return (
    <div>
      <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <FlaskConical className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        How this work is approached
      </p>

      {!workType ? (
        <div className="border border-dashed border-[var(--mustard)] bg-[var(--mustard-dim)]/60 dark:border-[var(--mustard)] dark:bg-[var(--mustard)]/20 px-3 py-2.5">
          <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
            No work type set. Assign one in the <span className="font-semibold">Work Type</span>{' '}
            section above so planning knows how this work is approached — until then this item
            produces a work unit with no work type and cannot be scheduled.
          </p>
        </div>
      ) : (
        <ExecutionRecipeBody workType={workType} label={label!} loading={loading} loaded={loaded} getRecipeByWorkType={getRecipeByWorkType} />
      )}
    </div>
  );
}

function ExecutionRecipeBody({
  workType,
  label,
  loading,
  loaded,
  getRecipeByWorkType,
}: {
  workType: string;
  label: string;
  loading: boolean;
  loaded: boolean;
  getRecipeByWorkType: ReturnType<typeof useExecutionRecipeStore.getState>['getRecipeByWorkType'];
}) {
  const recipe = getRecipeByWorkType(workType);

  if (loading && !loaded) {
    return <p className="text-xs text-[var(--text-ter)] italic">Loading…</p>;
  }

  if (!recipe) {
    return (
      <div className="border border-[var(--rule)] px-3 py-2.5">
        <p className="text-xs text-[var(--text-ter)]">
          Work type <span className="font-semibold">{label}</span> has no approach set yet. Add it
          under Settings → Work Types if this is a custom type.
        </p>
      </div>
    );
  }

  const blocking = recipe.required_process_artifacts.filter((a) => a.blocking);
  const recommended = recipe.required_process_artifacts.filter((a) => !a.blocking);

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 bg-[var(--surface)]/80 dark:bg-[var(--bg-raised)]/50 border-b border-[var(--rule)]">
        <Wrench className="h-4 w-4 text-[var(--text-ter)]" />
        <span className="text-sm font-semibold text-[var(--text-pri)]">{recipe.name}</span>
        <span className="inline-flex items-center rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] px-2 py-0.5 text-[11px] font-medium">
          {APPROACH_KIND_LABELS[recipe.approach_kind] ?? recipe.approach_kind}
        </span>
        <span
          className="inline-flex items-center rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] px-2 py-0.5 text-[11px] font-medium"
          title="Uncertainty multiplier — applied to effort estimates in Planning's capacity feasibility check"
        >
          ×{recipe.uncertainty_multiplier} uncertainty
        </span>
      </div>

      <div className="px-3 py-2.5 space-y-3">
        {recipe.description && (
          <p className="text-xs text-[var(--text-ter)] leading-relaxed">{recipe.description}</p>
        )}

        {/* Required (blocking) artifacts */}
        {blocking.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-[var(--mustard)]" />
              Required before scheduling
            </p>
            <ul className="space-y-1.5">
              {blocking.map((a) => (
                <li key={a.artifact_id} className="flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="text-xs font-medium text-[var(--text-sec)]">{a.label}</span>
                    <span className="block text-[11px] text-[var(--text-ter)] leading-snug">{a.description}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommended (non-blocking) artifacts */}
        {recommended.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <CircleDashed className="h-3 w-3 text-[var(--text-ter)]" />
              Recommended
            </p>
            <ul className="space-y-1.5">
              {recommended.map((a) => (
                <li key={a.artifact_id} className="flex items-start gap-2">
                  <CircleDashed className="h-3.5 w-3.5 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="text-xs font-medium text-[var(--text-sec)]">{a.label}</span>
                    <span className="block text-[11px] text-[var(--text-ter)] leading-snug">{a.description}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Governance policies */}
        {recipe.governance_policy_refs.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <ShieldCheck className="h-3 w-3 text-[var(--text-ter)]" />
              Governance
            </p>
            <ul className="space-y-1.5">
              {recipe.governance_policy_refs.map((p) => (
                <li key={p.policy_id} className="flex items-start gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="text-xs font-medium text-[var(--text-sec)]">{p.label}</span>
                    <span className="block text-[11px] text-[var(--text-ter)] leading-snug">{p.description}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExecutionRecipePanel;
